import { Captions } from '$components/Hero/Captions';
import { HeroOverlay } from '$components/Hero/HeroOverlay';
import { Leader } from '$components/Hero/Leader';
import { HeroContext } from '$components/Hero/context';
import { ZeroElement } from '$components/ZeroElement';
import { responsiveContentfulImage } from '$providers/Assets';
import { LayoutContext } from '$root/src/context';
import cn from 'classnames';
import { HTMLAttributes, useContext, useEffect, useRef, useState } from 'react';

import ReactPlayer from '../ReactPlayer/src/index';
import { Titles } from './Titles';
import { IHeroSlide } from './types';

export function HeroSlideVideo(
  props: IHeroSlide & HTMLAttributes<HTMLElement>,
) {
  const { placeholder } = props;
  const useStore = useContext(HeroContext);
  const useLayoutStore = useContext(LayoutContext);
  const setMenuTheme = useLayoutStore((state) => state.setMenuTheme);
  const windowScroll = useLayoutStore((state) => state.windowScroll);

  const currentPage = useStore((state) => state.currentPage);
  const fromBG = useStore((state) => state.fromBG);
  const firstLoad = useStore((state) => state.firstLoad);
  const { className, num, children, slideAppearance } = props;
  const finalAsset =
    typeof children !== 'undefined' && children !== null
      ? children.toString()
      : '';

  useEffect(() => {
    if (currentPage === num && windowScroll <= 0) {
      setMenuTheme(props.slideType);
    }
  }, [windowScroll, currentPage, num, props.slideType, setMenuTheme]);

  const [canPlayVideo, setCanPlayVideo] = useState(false);
  const [videoPlayerActive, setVideoPlayerActive] = useState(false);
  const [lightImage, setLightImage] = useState(true);

  const refReactPlayer = useRef(null);
  const player = useRef(null);
  const initPlayer = () => {
    setVideoPlayerActive(true);
  };

  useEffect(() => {
    if (
      (currentPage + 1 === num || currentPage === num) &&
      videoPlayerActive &&
      canPlayVideo
    ) {
      player.current = refReactPlayer.current.getInternalPlayer('hls');
      if (player.current) player.current.startLoad();
      else if (refReactPlayer.current)
        refReactPlayer.current.getInternalPlayer().play();
    }
  }, [currentPage, num, canPlayVideo, videoPlayerActive]);

  useEffect(() => {
    if (num === fromBG) {
      const timer = setTimeout(() => {
        setCanPlayVideo(false);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (currentPage + 1 === num || currentPage === num) {
      setCanPlayVideo(true);

      if (num === 0 || num === 1) {
        const delayTimer = setTimeout(() => {
          setLightImage(false);
        }, 1500);

        return () => clearTimeout(delayTimer);
      } else {
        setLightImage(false);
      }
    }
  }, [fromBG, currentPage, num]);

  const fadeOut =
    !firstLoad &&
    Math.abs(num - fromBG) <= 1 &&
    !(
      (currentPage < fromBG && fromBG < num) ||
      (currentPage < num && fromBG > num) ||
      (currentPage > num && fromBG < num) ||
      (currentPage > fromBG && fromBG > num)
    );

  return (
    <div
      key={num}
      id={`slide-${num + 1}`}
      className={cn(
        className,
        slideAppearance === 'insideRingFluid' ||
          slideAppearance === 'insideRingStatic'
          ? 'bg-white'
          : '',
        'p-gutter-mobile md:p-gutter-desktop w-full flex flex-col justify-center bg-no-repeat bg-cover absolute t-0 l-0',
        currentPage !== num ? 'pointer-events-none' : '',
        currentPage === num ? 'fadeIn' : fadeOut ? 'fadeOut' : '',
        'h-full',
      )}
      style={{
        opacity: currentPage === num ? '0' : fadeOut ? '1' : '0',
      }}
    >
      <div className={cn('absolute top-0 left-0 w-full h-full')}>
        {slideAppearance !== 'insideRingFluid' &&
          slideAppearance !== 'insideRingStatic' && (
            <>
              <ReactPlayer
                onReady={initPlayer}
                ref={refReactPlayer}
                url={finalAsset}
                className={
                  'absolute top-0 left-0 object-fill flex items-center justify-center'
                }
                autoPlay={canPlayVideo}
                width={'100%'}
                height={'100%'}
                playing={canPlayVideo}
                controls={false}
                loop={true}
                muted={true}
                playsinline={true}
                light={lightImage}
                config={{
                  file: {
                    hlsOptions: {
                      startLevel: 4,
                      autoStartLoad: false,
                    },
                    attributes: {
                      className: 'video-child',
                      preload: 'none',
                    },
                  },
                }}
              />
              {placeholder !== null && (
                <div
                  style={{
                    backgroundImage:
                      'url("' +
                      responsiveContentfulImage({
                        url: placeholder.url,
                        w: Math.floor(
                          1440 / Math.min(2, window.devicePixelRatio),
                        ),
                      }) +
                      '")',
                  }}
                  className={cn(
                    'absolute top-0 left-0 w-full pointer-events-none h-full bg-cover bg-center duration-[400ms] delay-[450ms]',
                    lightImage ? 'opacity-100' : 'opacity-0',
                  )}
                ></div>
              )}
            </>
          )}
      </div>
      <div className={cn('relative z-20 overflow-hidden')}>
        <Leader {...props} />
        <Titles {...props} />
        <Captions currentPage={currentPage} {...props} />
      </div>
      {slideAppearance !== 'noRing' && (
        <>
          {(slideAppearance === 'insideRingFluid' ||
            slideAppearance === 'insideRingStatic') && (
            <div
              key={num}
              className={cn(
                num === currentPage ? 'opacity-100' : 'opacity-0',
                'productSwirlContainer flex-col absolute left-0 top-0 w-full md:h-full flex md:justify-center items-center lg:overflow-hidden duration-[1600ms] h-full',
              )}
            >
              <div
                style={{
                  animationName:
                    num === currentPage
                      ? fromBG < num
                        ? 'spinner_page'
                        : 'spinner_page_backward'
                      : '',
                }}
                className={cn('outerRing absolute left-0 top-0 w-full h-full')}
              >
                <ZeroElement
                  index={num}
                  placeholderImage={placeholder}
                  canPlayVideo={canPlayVideo}
                  assetType={'video'}
                  type={
                    slideAppearance === 'insideRingFluid'
                      ? 'fluid'
                      : slideAppearance === 'insideRingStatic'
                      ? 'static'
                      : 'empty'
                  }
                  imageAsset={null}
                  videoAsset={finalAsset}
                />
                {props.overlayText !== '' && (
                  <HeroOverlay {...props} currentPage={currentPage} num={num} />
                )}
              </div>
            </div>
          )}
          {slideAppearance !== 'insideRingFluid' &&
            slideAppearance !== 'insideRingStatic' && (
              <ZeroElement
                index={num}
                placeholderImage={placeholder}
                canPlayVideo={canPlayVideo}
                assetType={'video'}
                type={'empty'}
                imageAsset={null}
                videoAsset={null}
              />
            )}
        </>
      )}
    </div>
  );
}
